// @archigraph r8DljQiU

import { create } from 'zustand';
import {
  cognitoSignUp,
  cognitoConfirmSignUp,
  cognitoResendConfirmation,
  cognitoSignIn,
  cognitoSignOut,
  cognitoRefreshSession,
} from '../services/cognito';
import type { CognitoUserInfo, CognitoTokens } from '../services/cognito';
import { saveTokens, loadTokens, clearTokens, isTokenExpired } from '../services/tokenStorage';

export interface AuthState {
  isAuthenticated: boolean;
  user: CognitoUserInfo | null;
  tokens: CognitoTokens | null;
  isLoading: boolean;
  error: string | null;

  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  signup: (email: string, password: string, name: string) => Promise<{ username: string; codeDeliveryDestination: string }>;
  confirmSignup: (username: string, code: string) => Promise<void>;
  resendCode: (username: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<boolean>;
  checkAuth: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  isAuthenticated: false,
  user: null,
  tokens: null,
  isLoading: true,
  error: null,

  login: async (email: string, password: string, rememberMe = true) => {
    set({ isLoading: true, error: null });
    try {
      const { tokens, user } = await cognitoSignIn(email, password);
      saveTokens(tokens, rememberMe);
      set({
        isAuthenticated: true,
        user,
        tokens,
        isLoading: false,
        error: null,
      });
    } catch (err) {
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : 'Login failed.',
      });
      throw err;
    }
  },

  signup: async (email: string, password: string, name: string) => {
    set({ isLoading: true, error: null });
    try {
      const result = await cognitoSignUp(email, password, name);
      set({ isLoading: false, error: null });
      return { username: result.username, codeDeliveryDestination: result.codeDeliveryDestination };
    } catch (err) {
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : 'Signup failed.',
      });
      throw err;
    }
  },

  confirmSignup: async (username: string, code: string) => {
    set({ isLoading: true, error: null });
    try {
      await cognitoConfirmSignUp(username, code);
      set({ isLoading: false, error: null });
    } catch (err) {
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : 'Confirmation failed.',
      });
      throw err;
    }
  },

  resendCode: async (username: string) => {
    set({ isLoading: true, error: null });
    try {
      await cognitoResendConfirmation(username);
      set({ isLoading: false, error: null });
    } catch (err) {
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to resend code.',
      });
      throw err;
    }
  },

  logout: async () => {
    set({ isLoading: true });
    try {
      await cognitoSignOut();
    } catch {
      // Sign out locally even if Cognito call fails
    }
    clearTokens();
    set({
      isAuthenticated: false,
      user: null,
      tokens: null,
      isLoading: false,
      error: null,
    });
  },

  refreshSession: async () => {
    try {
      const result = await cognitoRefreshSession();
      if (result) {
        const rememberMe = localStorage.getItem('archigraph_remember_me') === 'true';
        saveTokens(result.tokens, rememberMe);
        set({
          isAuthenticated: true,
          user: result.user,
          tokens: result.tokens,
          error: null,
        });
        return true;
      }
      return false;
    } catch {
      return false;
    }
  },

  checkAuth: async () => {
    set({ isLoading: true });

    const storedTokens = loadTokens();
    if (!storedTokens) {
      set({ isLoading: false, isAuthenticated: false, user: null, tokens: null });
      return;
    }

    if (isTokenExpired()) {
      // Attempt to refresh
      const refreshed = await get().refreshSession();
      if (!refreshed) {
        clearTokens();
        set({
          isLoading: false,
          isAuthenticated: false,
          user: null,
          tokens: null,
        });
        return;
      }
    } else {
      // Token still valid — restore from Cognito SDK session
      const result = await cognitoRefreshSession();
      if (result) {
        set({
          isAuthenticated: true,
          user: result.user,
          tokens: result.tokens,
          isLoading: false,
        });
        return;
      }
      // Fallback: tokens exist but can't get user info
      clearTokens();
      set({
        isLoading: false,
        isAuthenticated: false,
        user: null,
        tokens: null,
      });
    }
  },

  clearError: () => set({ error: null }),
}));
